<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { computed, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue';
import Button from 'primevue/button';
import AuthLoadingState from '@/components/ui/AuthLoadingState.vue';
import { PRODUCT_TOUR_WELCOME, type ProductTourStep } from '@/constants/productTour';
import { useTourPosition } from '@/composables/useTourPosition';

const props = defineProps<{
    step: ProductTourStep | null;
    progress: number;
    total: number;
    navigating: boolean;
    loading: boolean;
}>();
const emit = defineEmits<{
    next: [];
    dismiss: [];
}>();

const dialog = shallowRef<HTMLDialogElement | null>(null);
const card = shallowRef<HTMLElement | null>(null);
const heading = shallowRef<HTMLElement | null>(null);
const currentStep = computed(() => props.step);
const LOADING_MESSAGE = 'Starting your tour';
const title = computed(() => props.step?.title ?? PRODUCT_TOUR_WELCOME.title);
const actionLabel = computed(() => {
    if (!props.step) return 'Take the tour';
    return props.progress === props.total ? 'Finish' : 'Next';
});
const { spotlightStyle, cardPosition } = useTourPosition(currentStep, card);
let previousFocus: HTMLElement | null = null;
let previousOverflow = '';
let previousPadding: string | null = null;
let previousScroll = { left: 0, top: 0 };

onMounted(() => {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    previousOverflow = document.body.style.overflow;
    previousScroll = { left: window.scrollX, top: window.scrollY };
    document.body.style.overflow = 'hidden';
    // The gutter <html> reserves sits outside the paintable viewport, so no overlay can dim it.
    // Drop it for the tour and give the width straight back as padding: nothing moves.
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    if (gutter > 0) {
        previousPadding = document.body.style.paddingRight;
        document.documentElement.style.setProperty('scrollbar-gutter', 'auto', 'important');
        document.body.style.paddingRight = `${gutter}px`;
    }
    dialog.value?.showModal();
    heading.value?.focus({ preventScroll: true });
});

watch(currentStep, () => heading.value?.focus({ preventScroll: true }), { flush: 'post' });

onBeforeUnmount(() => {
    dialog.value?.close();
    document.body.style.overflow = previousOverflow;
    if (previousPadding !== null) {
        document.documentElement.style.removeProperty('scrollbar-gutter');
        document.body.style.paddingRight = previousPadding;
    }
    window.scrollTo({ ...previousScroll, behavior: 'auto' });
    const fallback = document.querySelector<HTMLElement>('[data-product-tour="help"]');
    const returnTarget = previousFocus?.isConnected && previousFocus !== document.body ? previousFocus : fallback;
    returnTarget?.focus({ preventScroll: true });
});
</script>

<template>
    <Teleport to="body">
        <dialog
            ref="dialog"
            class="product-tour"
            :class="{ 'product-tour--loading': loading }"
            :aria-label="loading ? LOADING_MESSAGE : undefined"
            :aria-labelledby="loading ? undefined : 'product-tour-title'"
            :aria-describedby="loading ? undefined : 'product-tour-body'"
            @cancel.prevent="emit('dismiss')"
        >
            <div v-if="loading" class="product-tour__loading">
                <AuthLoadingState :message="LOADING_MESSAGE" />
            </div>

            <template v-else>
                <div v-if="spotlightStyle" class="product-tour__spotlight" :style="spotlightStyle" aria-hidden="true" />
                <div v-else class="product-tour__shade" aria-hidden="true" />

                <section
                    ref="card"
                    class="product-tour__card"
                    :class="{
                        'product-tour__card--wide': !step,
                        'product-tour__card--centered': !cardPosition,
                    }"
                    :style="cardPosition ?? undefined"
                >
                    <div class="product-tour__header">
                        <span id="product-tour-title" ref="heading" class="product-tour__title" tabindex="-1">
                            {{ title }}
                        </span>
                        <button
                            type="button"
                            class="product-tour__close"
                            aria-label="Close product tour"
                            @click="emit('dismiss')"
                        >
                            <i class="pi pi-times" aria-hidden="true" />
                        </button>
                    </div>

                    <span class="product-tour__hr" aria-hidden="true" />

                    <div id="product-tour-body" class="product-tour__body">
                        <p class="product-tour__copy">{{ step ? step.body : PRODUCT_TOUR_WELCOME.body }}</p>
                    </div>

                    <span class="product-tour__hr" aria-hidden="true" />

                    <div class="product-tour__footer">
                        <span v-if="step" class="product-tour__progress" aria-live="polite">{{ progress }} of {{ total }}</span>
                        <Button
                            type="button"
                            class="product-tour__cancel-btn"
                            :label="step ? 'Skip' : 'Skip for now'"
                            @click="emit('dismiss')"
                        />
                        <Button
                            type="button"
                            class="product-tour__action-btn"
                            :label="actionLabel"
                            :disabled="navigating"
                            @click="emit('next')"
                        />
                    </div>
                </section>
            </template>
        </dialog>
    </Teleport>
</template>

<style lang="scss" scoped>
.product-tour {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100dvh;
    max-width: none;
    max-height: none;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: $black-color;
    overflow: hidden;
    font-family: $font-family-archivo;

    &::backdrop { background: transparent; }

    &__shade {
        position: absolute;
        inset: 0;
        background: rgba($black-color, 0.56);
    }

    &__loading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba($main-background-color, 0.75);
    }

    &__spotlight {
        position: fixed;
        border: 2px solid $white-color;
        border-radius: 12px;
        box-shadow: 0 0 0 100vmax rgba($black-color, 0.56);
        pointer-events: none;
    }

    &__card {
        position: fixed;
        display: flex;
        flex-direction: column;
        width: min(420px, calc(100% - 24px));
        max-height: calc(100dvh - 24px);
        overflow-y: auto;
        padding: 30px 40px;
        border: 1px solid $paginator-border-color;
        border-radius: 5px;
        background: $white-color;
        box-shadow: 0 0 30px rgba($black-color, 0.1);

        &--wide { width: min(600px, calc(100% - 24px)); }

        &--centered {
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
        }
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
    }

    &__title {
        font-family: $font-family-archivo;
        font-weight: 500;
        font-size: 20px;
        line-height: 26px;
        color: $black-color;
        outline: none;
    }

    &__close {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
        color: $main-orange;

        .pi {
            font-size: 16px;
            font-weight: 900;
        }
    }

    &__hr {
        display: block;
        width: 100%;
        height: 2px;
        margin: 20px 0;
        background: $hr-gray-color;
    }

    &__body {
        flex: 1;
        min-height: 0;
    }

    &__copy {
        margin: 0;
        color: rgba($black-color, 0.75);
        font-size: 16px;
        line-height: 26px;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
        padding-top: 10px;
    }

    &__progress {
        margin-right: auto;
        color: rgba($black-color, 0.65);
        font-size: 14px;
        line-height: 20px;
        font-variant-numeric: tabular-nums;
    }

    &__cancel-btn,
    &__action-btn {
        min-width: 91px;
        height: 46px !important;
        border-radius: 10px !important;
        font-family: $font-family-archivo !important;
        font-size: 16px !important;
        line-height: 17px !important;
        box-shadow: none !important;
        padding: 0 16px !important;
        justify-content: center;
    }

    &__cancel-btn {
        border: 1px solid $border-gray-color !important;
        background: $white-color !important;
        font-weight: 400 !important;
        color: rgba($black-color, 0.7) !important;
    }

    &__action-btn {
        border: 1px solid $main-orange !important;
        background: $main-orange !important;
        font-weight: 600 !important;
        color: $white-color !important;

        &:hover:not(:disabled) {
            background: $main-orange-hover !important;
            border-color: $main-orange-hover !important;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    @media (max-width: 480px) {
        &__card { padding: 24px 20px; }
        &__title { font-size: 18px; }
    }
}
</style>

<style lang="scss">
html:has(dialog.product-tour[open]:not(.product-tour--loading)) {
    background-color: mix($black-color, $main-background-color, 56%);
}
</style>
